<script setup>
import { useCampaignAvailability } from 'dashboard/composables/useCampaignAvailability';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import EditWhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/EditWhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import CampaignDetailsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/CampaignDetailsDialog.vue';
import DeliveryErrorsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/DeliveryErrorsDialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const createCampaignDialogRef = ref(null);
const editCampaignDialogRef = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);
const confirmStopCampaignDialogRef = ref(null);
const campaignDetailsDialogRef = ref(null);
const deliveryErrorsDialogRef = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const openCreateDialog = () => {
  createCampaignDialogRef.value?.dialogRef?.open();
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value?.dialogRef?.open();
};

const handleEdit = campaign => {
  selectedCampaign.value = campaign;
  editCampaignDialogRef.value?.dialogRef?.open();
};

const handleViewDetails = campaign => {
  router.push({
    name: 'campaign_whatsapp_detail',
    params: {
      accountId: route.params.accountId,
      campaignId: campaign.id,
    },
  });
};

const handlePause = async campaign => {
  try {
    await CampaignsAPI.pause(campaign.id);
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.PAUSE_SUCCESS'));
    store.dispatch('campaigns/get');
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.PAUSE_ERROR'));
  }
};

const handleResume = async campaign => {
  try {
    await CampaignsAPI.resume(campaign.id);
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.RESUME_SUCCESS'));
    store.dispatch('campaigns/get');
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.RESUME_ERROR'));
  }
};

const handleStop = campaign => {
  selectedCampaign.value = campaign;
  confirmStopCampaignDialogRef.value?.open();
};

const confirmStop = async () => {
  try {
    await CampaignsAPI.stop(selectedCampaign.value.id);
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_SUCCESS'));
    store.dispatch('campaigns/get');
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_ERROR'));
  } finally {
    confirmStopCampaignDialogRef.value?.close();
  }
};

const handleViewErrorsFromDetails = () => {
  deliveryErrorsDialogRef.value?.dialogRef?.open();
};
const { campaignsEnabled } = useCampaignAvailability();
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="openCreateDialog"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      @edit="handleEdit"
      @delete="handleDelete"
      @view-details="handleViewDetails"
      @pause="handlePause"
      @resume="handleResume"
      @stop="handleStop"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <Dialog
      ref="confirmStopCampaignDialogRef"
      type="alert"
      :title="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_CONFIRM.TITLE')"
      :description="
        t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_CONFIRM.DESCRIPTION', {
          campaignName: selectedCampaign?.title || '',
        })
      "
      :confirm-button-label="
        t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_CONFIRM.CONFIRM')
      "
      @confirm="confirmStop"
    />
  </CampaignLayout>
  <WhatsAppCampaignDialog
    v-if="campaignsEnabled"
    ref="createCampaignDialogRef"
  />
  <EditWhatsAppCampaignDialog
    v-if="campaignsEnabled"
    ref="editCampaignDialogRef"
    :selected-campaign="selectedCampaign"
  />
  <CampaignDetailsDialog
    ref="campaignDetailsDialogRef"
    :campaign="selectedCampaign"
    @view-errors="handleViewErrorsFromDetails"
  />
  <DeliveryErrorsDialog
    ref="deliveryErrorsDialogRef"
    :campaign="selectedCampaign"
  />
</template>
